/**
 * useMakeReadySummary — shared make-ready counts for the nav badge + list
 * page chips (spec 99 W1, mirroring `useAlertSummary`).
 *
 * Polls the cheap counts-only `GET /make-ready/summary` every 60s + on window
 * focus. The Resident Moves nav badge and the Make-Ready list page consume
 * this ONE query (same key → TanStack Query dedupes to a single request).
 *
 * Enabled for every org role — `/make-ready` is an ALL_PORTAL route (D11:
 * VIEWER sees the list read-only) — and for ops users actively viewing an
 * org. Disabled for logged-out / bare-ops sessions so we never poll blind.
 */
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

export interface MakeReadySummaryCounts {
  active: number;
  needsAction: number;
  atRisk: number;
  breached: number;
  compressed: number;
  mine: number;
  done: number;
  /** Called-off turns — the second terminal bucket, beside `done`. */
  cancelled: number;
}

const ZERO: MakeReadySummaryCounts = {
  active: 0,
  needsAction: 0,
  atRisk: 0,
  breached: 0,
  compressed: 0,
  mine: 0,
  done: 0,
  cancelled: 0,
};

/**
 * What the LIST page is currently looking at. Omitted by the nav badge, which
 * wants the org-wide total — that is what a badge is.
 *
 * Passing these is what stops the chips contradicting the table beneath them:
 * deep-linking `?propertyId=` from the by-property report drew "Active turns
 * 12" directly above two rows, and the chips are also the filter control, so
 * the number a user pressed was not the number they got back.
 *
 * Deliberately NOT the chip selections themselves (health / needsAction /
 * mine): those are the buckets being counted.
 */
export interface MakeReadySummaryScope {
  propertyIds?: string[];
  stage?: string | null;
  turnKind?: string | null;
  search?: string;
}

export function useMakeReadySummary(scope?: MakeReadySummaryScope) {
  const { user, isOpsUser, opsOrgAccess } = useAuth();
  const enabled =
    (!isOpsUser && user?.type === 'org') || (isOpsUser && !!opsOrgAccess);

  const params: Record<string, string> = {};
  if (scope?.propertyIds?.length) params.propertyIds = scope.propertyIds.join(',');
  if (scope?.stage) params.stage = scope.stage;
  if (scope?.turnKind) params.turnKind = scope.turnKind;
  if (scope?.search?.trim()) params.search = scope.search.trim();

  const query = useQuery({
    // The key carries the scope, so the badge's org-wide query and the list's
    // scoped one are separate cache entries rather than one clobbering the
    // other. With no scope the key is unchanged, so the badge still shares a
    // single request across every consumer.
    queryKey: ['make-ready-summary', params],
    queryFn: async (): Promise<MakeReadySummaryCounts> => {
      const res = await api.get('/make-ready/summary', { params });
      const c = res.data?.data?.counts ?? res.data?.counts ?? {};
      return {
        active: c.active ?? 0,
        needsAction: c.needsAction ?? 0,
        atRisk: c.atRisk ?? 0,
        breached: c.breached ?? 0,
        compressed: c.compressed ?? 0,
        mine: c.mine ?? 0,
        done: c.done ?? 0,
        cancelled: c.cancelled ?? 0,
      };
    },
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  return { counts: query.data ?? ZERO, isLoading: query.isLoading, enabled };
}
